import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { featuresAPI } from '../services/api/featuresAPI.js';
import { hasFeature as checkFeature } from '../utils/features.js';
import { tenantStorageKey } from '../tenancy/authStorage.js';

const cacheCapabilities = (capabilities) => {
  try {
    const key = tenantStorageKey('features');
    const serialized = JSON.stringify(capabilities);
    const changed = sessionStorage.getItem(key) !== serialized;
    sessionStorage.setItem(key, serialized);
    if (changed && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('atrux:permissions-changed'));
    }
  } catch {
    // Feature data can always be fetched again.
  }
};

const clearCachedCapabilities = () => {
  try {
    sessionStorage.removeItem(tenantStorageKey('features'));
  } catch {
    // Ignore unavailable storage.
  }
};

const FeatureFlagsContext = createContext({
  capabilities: null,
  role: null,
  features: null,
  loading: false,
  error: '',
  refreshCapabilities: async () => null,
  refreshFeatures: async () => null,
  hasFeature: () => false,
});

export const FeatureFlagsProvider = ({ authToken, children }) => {
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(Boolean(authToken));
  const [error, setError] = useState('');
  const features = capabilities?.features || null;
  const role = capabilities?.role || null;

  const loadCapabilities = useCallback(async () => {
    if (!authToken) {
      clearCachedCapabilities();
      setCapabilities(null);
      setError('');
      setLoading(false);
      return null;
    }

    setLoading(true);
    setError('');

    try {
      const nextCapabilities = await featuresAPI.getMyCapabilities(authToken);
      setCapabilities(nextCapabilities);
      cacheCapabilities(nextCapabilities);
      return nextCapabilities;
    } catch (err) {
      setCapabilities(null);
      setError(err.message || 'Nu am putut încărca feature flags.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!authToken) {
        setCapabilities(null);
        setError('');
        setLoading(false);
        return;
      }

      // Never reuse another account's in-memory capabilities while the token changes.
      setCapabilities(null);
      setLoading(true);
      setError('');

      try {
        const nextCapabilities = await featuresAPI.getMyCapabilities(authToken);
        if (!cancelled) {
          setCapabilities(nextCapabilities);
          cacheCapabilities(nextCapabilities);
        }
      } catch (err) {
        if (!cancelled) {
          setCapabilities(null);
          setError(err.message || 'Nu am putut încărca feature flags.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [authToken]);

  useEffect(() => {
    const refresh = () => loadCapabilities();
    window.addEventListener('atrux:features-stale', refresh);
    return () => window.removeEventListener('atrux:features-stale', refresh);
  }, [loadCapabilities]);

  const refreshFeatures = useCallback(async () => {
    const nextCapabilities = await loadCapabilities();
    return nextCapabilities?.features || null;
  }, [loadCapabilities]);

  const value = useMemo(() => ({
    capabilities,
    role,
    features,
    loading,
    error,
    refreshCapabilities: loadCapabilities,
    refreshFeatures,
    hasFeature: (code) => checkFeature(features, code),
  }), [capabilities, role, features, loading, error, loadCapabilities, refreshFeatures]);

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => useContext(FeatureFlagsContext);
